import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';

// Initialize dayjs plugins
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Converts a UTC date string to a dayjs object in the specified timezone
 * @param {string} utcDateString - UTC date string (ISO format)
 * @param {string} timezone - Target timezone (e.g., 'Asia/Manila', 'UTC')
 * @returns {dayjs.Dayjs|null} - dayjs object in the target timezone, or null if invalid
 */
export const utcToDisplayTimezone = (utcDateString, timezone) => {
  if (!utcDateString) return null;
  // If timezone is already UTC, no conversion needed
  if (!timezone || timezone === 'UTC') {
    return dayjs(utcDateString);
  }
  try {
    const utcDate = new Date(utcDateString);
    const zonedDate = utcToZonedTime(utcDate, timezone);
    return dayjs(zonedDate);
  } catch (e) {
    console.warn('Error converting UTC to display timezone:', e);
    return dayjs(utcDateString);
  }
};

/**
 * Converts a dayjs object (interpreted as being in the specified timezone) to UTC ISO string
 * This function interprets the dayjs object's time as being in the specified timezone,
 * then converts it to UTC for the API
 * @param {dayjs.Dayjs} dayjsObj - dayjs object representing a time in the source timezone
 * @param {string} timezone - Source timezone (e.g., 'Asia/Manila', 'UTC')
 * @returns {string|null} - UTC ISO string, or null if invalid
 */
export const timezoneToUtc = (dayjsObj, timezone) => {
  if (!dayjsObj || !dayjsObj.isValid()) return null;
  // If timezone is already UTC, no conversion needed - return as-is
  if (!timezone || timezone === 'UTC') {
    return dayjsObj.utc().toISOString();
  }
  try {
    // Use dayjs timezone plugin to set the timezone, then convert to UTC
    // This ensures the time is interpreted as being in the specified timezone
    const utcDate = dayjsObj.tz(timezone).utc();
    return utcDate.toISOString();
  } catch (e) {
    console.warn('Error converting timezone to UTC:', e);
    // Fallback: create date string and use zonedTimeToUtc
    try {
      const dateString = dayjsObj.format('YYYY-MM-DDTHH:mm:ss');
      const localDate = new Date(dateString);
      const utcDate = zonedTimeToUtc(localDate, timezone);
      return utcDate.toISOString();
    } catch (e2) {
      console.warn('Fallback conversion also failed:', e2);
      return dayjsObj.utc().toISOString();
    }
  }
};

/**
 * Alias for timezoneToUtc - converts display timezone dayjs object to UTC ISO string
 * This is a convenience function that uses the same logic as timezoneToUtc
 * @param {dayjs.Dayjs} dayjsObj - dayjs object representing a time in the display timezone
 * @param {string} displayTimezone - Display timezone (e.g., 'Asia/Manila', 'UTC')
 * @returns {string|null} - UTC ISO string, or null if invalid
 */
export const displayTimezoneToUtc = (dayjsObj, displayTimezone) => {
  return timezoneToUtc(dayjsObj, displayTimezone);
};

/**
 * Converts a datetime-local string (YYYY-MM-DDTHH:mm format) to UTC ISO string
 * The input string is interpreted as being in the specified timezone
 * @param {string} datetimeLocalStr - Datetime string in format "YYYY-MM-DDTHH:mm" or "YYYY-MM-DDTHH:mm:ss"
 * @param {string} timezone - Source timezone (e.g., 'Asia/Manila', 'UTC')
 * @returns {string|null} - UTC ISO string, or null if invalid
 */
export const displayTimezoneToUtcISO = (datetimeLocalStr, timezone) => {
  if (!datetimeLocalStr) return null;
  try {
    // datetime-local input is in the specified timezone, parse it as such
    const localDate = new Date(datetimeLocalStr);
    // Convert from the specified timezone to UTC
    if (!timezone || timezone === 'UTC') {
      return localDate.toISOString();
    }
    const utcDate = zonedTimeToUtc(localDate, timezone);
    return utcDate.toISOString();
  } catch (e) {
    console.warn('Error converting display timezone to UTC:', e);
    return new Date(datetimeLocalStr).toISOString();
  }
};

